import type { Api } from '@rialtic/api'
import { AppConfigKeys } from '@rialtic/types'
import { defu } from 'defu'
import { clear, del, get, set } from 'idb-keyval'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { ALL_LOB } from '~/constants'
import type { ConnectorAnalyticsState } from '~/types'
import { useWorkspace } from './workspace'

// TODO: remove once WFL-6520 lands and counts_by_claim_type is typed in PaAPI generated types
interface ClaimTypeCounts {
  claim_with_insights_count: number
  insight_count: number
  insight_count_active: number
  insight_count_observation: number
  savings: number
  savings_active: number
  savings_observation: number
  impacted_providers: number
}

interface PolicyInsightCounts {
  id: string
  insight_count: number
  savings: number
  impacted_providers: number
  claim_count_with_insights_total: number
  counts_by_insight_mode?: {
    Active: {
      claim_count_with_insights: number
      insight_count: number
      savings_opportunity_cents: number
    }
    Observation: {
      claim_count_with_insights: number
      insight_count: number
      savings_opportunity_cents: number
    }
  }
  // TODO: remove once WFL-6520 lands
  counts_by_claim_type?: Record<string, ClaimTypeCounts>
  insight_count_total: number
  provider_impact?: {
    insights_count_median: number
    impacted_providers: number
    savings_opportunity_median: number
  }
  savings_opportunity_cents: number
}

interface ChartInsightItem {
  id: string
  name: string
  countActive: number
  countObservation: number
  claim_count_with_insights: number
  /* format "0.01" */
  rateActive: string
  /* format "0.01" */
  rateObservation: string
  savingsActive: number
  savingsObservation: number
}

type InsightItem = Api.ConnectorInsightCounts & {
  lastRefreshedAt?: number
  logicTypes?: ChartInsightItem[]
  // TODO: remove once WFL-6520 lands and counts_by_claim_type is in PaAPI generated types
  counts_by_claim_type?: Record<string, ClaimTypeCounts>
}

export interface ClaimTypeAnalyticsRow {
  id: string
  name: string
  savingsActive: number
  savingsObservation: number
  insightsActive: number
  insightsObservation: number
  claimTotal: number
}

export const sumClaimTypeRows = (
  rows: ClaimTypeAnalyticsRow[],
): Omit<ClaimTypeAnalyticsRow, 'id' | 'name'> =>
  rows.reduce(
    (acc, row) => ({
      savingsActive: acc.savingsActive + row.savingsActive,
      savingsObservation: acc.savingsObservation + row.savingsObservation,
      insightsActive: acc.insightsActive + row.insightsActive,
      insightsObservation: acc.insightsObservation + row.insightsObservation,
      claimTotal: acc.claimTotal + row.claimTotal,
    }),
    {
      savingsActive: 0,
      savingsObservation: 0,
      insightsActive: 0,
      insightsObservation: 0,
      claimTotal: 0,
    },
  )

const addClaimTypeCounts = (
  accumulated: ClaimTypeCounts,
  current: ClaimTypeCounts,
): ClaimTypeCounts => ({
  claim_with_insights_count:
    accumulated.claim_with_insights_count + current.claim_with_insights_count,
  insight_count: accumulated.insight_count + current.insight_count,
  insight_count_active:
    accumulated.insight_count_active + current.insight_count_active,
  insight_count_observation:
    accumulated.insight_count_observation + current.insight_count_observation,
  savings: accumulated.savings + current.savings,
  savings_active: accumulated.savings_active + current.savings_active,
  savings_observation:
    accumulated.savings_observation + current.savings_observation,
  impacted_providers:
    accumulated.impacted_providers + current.impacted_providers,
})

const toClaimTypeRows = (
  counts?: Record<string, ClaimTypeCounts>,
): ClaimTypeAnalyticsRow[] => {
  if (!counts) return []

  return Object.entries(counts).map(([name, c]) => ({
    id: name,
    name,
    savingsActive: c.savings_active / 100,
    savingsObservation: c.savings_observation / 100,
    insightsActive: c.insight_count_active,
    insightsObservation: c.insight_count_observation,
    claimTotal: c.claim_with_insights_count,
  }))
}

export type AggregateState = Omit<
  InsightItem,
  | 'connector_id'
  | 'date_range'
  | 'policies'
  | 'editTypeInsights'
  | 'topicInsights'
>
export const defaultAggregate: AggregateState = {
  claimTotal: 0,
  claimVolume: 0,
  countsActive: {
    claim_count_with_insights: 0,
    insight_count: 0,
    savings_opportunity_cents: 0,
  },
  countsObservation: {
    claim_count_with_insights: 0,
    insight_count: 0,
    savings_opportunity_cents: 0,
  },
  claims_with_recommendations_count: 0,
  claims_with_reporting_insights_count: 0,
  insightsActive: 0,
  insightsObservation: 0,
  rateActive: '0',
  rateObservation: '0',
  savingsActive: 0,
  savingsObservation: 0,
  savingsTotal: 0,
  counts_by_logic_type: {},
  logicTypes: [],
}

const getDefaultInsightCounts = (
  connector_id: string,
): ConnectorAnalyticsState => ({
  connector_id,
  aggregate: defaultAggregate,
  error: null,
  lastRefreshedAt: null,
  loading: false,
  policies: [],
  editTypeInsights: [],
  topicInsights: [],
  logicTypes: [],
  updated: Date.now(),
})

const mapChartInsightItem = (x: ChartInsightItem) => {
  const savingsObj = {
    savingsActive: x.savingsActive / 100,
    savingsObservation: x.savingsObservation / 100,
  }
  return { ...x, ...savingsObj }
}

export const useConnectorAnalytics = defineStore('connector-analytics', {
  state: () => {
    return {
      analyticsAbortControllers: new Map<string, AbortController>(),
      connectors: new Map<string, ConnectorAnalyticsState>(),
      isAggregateLoading: false,
      lastRefreshedAt: 0,
      nextRefreshEstimate: Date.now() + 1000 * 60 * 60,
      requestsInFlight: new Set<string>(),
      retryAfterTimestamp: Number.POSITIVE_INFINITY,
      syncConnectorAfter: Number.POSITIVE_INFINITY,
    }
  },

  getters: {
    chartEditTypeInsights: (state) => (connectorId: string) => {
      const analyticsData = state.connectors.get(connectorId)

      if (!analyticsData) {
        return []
      }

      const workspace = useWorkspace()
      const calcMethod = workspace.activeWorkspace?.impact_calculation_method

      // TODO: remove once Hiro is v9 on all connectors
      const useLogicTypeData = calcMethod === 'client' || calcMethod === 'sabr'
      if (useLogicTypeData) {
        return analyticsData.logicTypes?.map(mapChartInsightItem) ?? []
      }

      return analyticsData.editTypeInsights?.map(mapChartInsightItem) ?? []
    },

    chartTopicInsights: (state) => (connectorId: string) =>
      state.connectors
        .get(connectorId)
        ?.topicInsights?.map(mapChartInsightItem) || [],

    displaySavings: (state) => (connectorId: string) => {
      const results = {
        savingsActive: {
          label: 'Active',
          value: ['-', '-'],
        },
        savingsObserving: {
          label: 'Observation',
          value: ['-', '-'],
        },
      }

      const aggregate =
        state.connectors.get(connectorId)?.aggregate || defaultAggregate

      results.savingsActive.value = [
        formatCents(aggregate.savingsActive, NumeralFormats.DASH_DOLLARS),
        formatRate(aggregate.savingsActive / aggregate.claimVolume),
      ]
      results.savingsObserving.value = [
        formatCents(aggregate?.savingsObservation, NumeralFormats.DASH_DOLLARS),
        formatRate(aggregate.savingsObservation / aggregate.claimVolume),
      ]

      return results
    },

    displayClaimsStats: (state) => (connectorId: string) => {
      const results = {
        claimVolume: {
          label: 'Claim volume',
          value: '-',
        },
        totalClaims: {
          label: 'Total claims',
          value: '-',
        },
      }

      const aggregate =
        state.connectors.get(connectorId)?.aggregate || defaultAggregate

      results.claimVolume.value =
        aggregate.claimVolume != null
          ? formatCents(aggregate.claimVolume, NumeralFormats.DASH_DOLLARS)
          : '-'

      results.totalClaims.value =
        aggregate.claimTotal != null
          ? formatCount(aggregate.claimTotal, NumeralFormats.CLAIM_TOTAL)
          : '-'

      return results
    },

    displayInsightStats: (state) => (connectorId: string) => {
      const aggregate =
        state.connectors.get(connectorId)?.aggregate || defaultAggregate

      return {
        rateActive: {
          label: 'Active',
          value: formatRate(aggregate.rateActive),
        },
        rateObservation: {
          label: 'Observation',
          value: formatRate(aggregate.rateObservation),
        },
      }
    },

    claimTypeBreakdownRows:
      (state) =>
      (
        connectorId?: string,
        connectorFilter?: Set<string>,
      ): ClaimTypeAnalyticsRow[] => {
        // Single connector
        if (connectorId) {
          return toClaimTypeRows(
            state.connectors.get(connectorId)?.aggregate.counts_by_claim_type,
          )
        }

        // Sum each claim type across connectors, respecting an optional filter
        const claimTypeTotals = new Map<string, ClaimTypeCounts>()
        for (const [id, { aggregate }] of state.connectors.entries()) {
          if (connectorFilter && !connectorFilter.has(id)) continue
          if (!aggregate.counts_by_claim_type) continue

          for (const [name, counts] of Object.entries(
            aggregate.counts_by_claim_type,
          )) {
            const accumulated = claimTypeTotals.get(name)
            claimTypeTotals.set(
              name,
              accumulated
                ? addClaimTypeCounts(accumulated, counts)
                : { ...counts },
            )
          }
        }
        return toClaimTypeRows(Object.fromEntries(claimTypeTotals))
      },

    loading: (state) => state.requestsInFlight.size > 0,

    policyInsightCounts: (state) => (connectorId: string) => {
      const map = new Map<string, PolicyInsightCounts>()

      const policies = state.connectors.get(connectorId)?.policies || []
      for (const policyData of policies) {
        map.set(policyData.id, {
          ...policyData,
          id: policyData.id,
          insight_count: policyData.insight_count_total || 0,
          savings: policyData?.savings_opportunity_cents,
          impacted_providers:
            policyData?.provider_impact?.impacted_providers || 0,
        })
      }

      return map
    },
  },

  actions: {
    clearCacheDb() {
      return clear()
    },

    clearCacheDbByKey(workspaceId: string, connectorId: string) {
      return del(`analytics_aggregate:${workspaceId}:${connectorId}`)
    },

    async fetchAnalyticsAggregate(
      workspaceId: string,
      connectorId: string,
    ): Promise<InsightItem | null> {
      if (connectorId === ALL_LOB) {
        return null
      }
      const { $datadog } = useNuxtApp()
      const workspace = useWorkspace()

      if (!workspace.muxIsConfigured || workspace.underMaintenance) {
        return null
      }

      const abortKey = `fetch_aggregate:${workspaceId}:${connectorId}`

      if (this.analyticsAbortControllers.has(abortKey)) {
        this.analyticsAbortControllers.get(abortKey)!.abort()
      }

      this.analyticsAbortControllers.set(abortKey, new AbortController())

      try {
        const featureFlags = useFeatureFlags()
        const api = $apiFetcher()
        const data = await api<InsightItem>(
          `/api/connector/${connectorId}/analytics-aggregate`,
          {
            params: featureFlags.isEnabled(
              AppConfigKeys.featureClaimTypeAnalyticsRows,
            )
              ? { ...workspace.muxFilters, claim_type_mock: true }
              : workspace.muxFilters,
            signal: this.analyticsAbortControllers.get(abortKey)?.signal,
          },
        )

        if (!data.connector_id) {
          return null
        }
        return data
      } catch (e) {
        if ((e as Error).name !== 'AbortError') {
          $datadog.addAction('analytics_aggregate_error', {
            workspaceId,
            connectorId,
            error: e instanceof Error ? e.message : e,
          })
        }

        return null
      } finally {
        this.analyticsAbortControllers.delete(abortKey)
      }
    },

    async getAnalyticsAggregate(
      workspaceId: string,
      connectorId: string,
    ): Promise<void> {
      if (!workspaceId || !connectorId || connectorId === ALL_LOB) {
        return
      }
      this.isAggregateLoading = true
      const workspace = useWorkspace()
      const { $datadog } = useNuxtApp()

      const inflightKey = `${connectorId}:${workspace.muxFilters.start_date}${workspace.muxFilters.end_date}`

      if (this.requestsInFlight.has(inflightKey)) {
        return
      }

      this.requestsInFlight.add(inflightKey)

      try {
        const KEY = `analytics_aggregate:${workspaceId}:${connectorId}`

        if (!workspace.muxIsConfigured) {
          this.setAnalyticsAggregate(connectorId, null)
          await workspace.waitForReady(3000)
        }

        if (import.meta.client) {
          const cachedData = await get<InsightItem | null>(KEY)
          this.setAnalyticsAggregate(connectorId, cachedData)
        }

        const data = await this.fetchAnalyticsAggregate(
          workspaceId,
          connectorId,
        )

        if (data) {
          this.setAnalyticsAggregate(connectorId, data)

          if (import.meta.client) {
            try {
              await set(KEY, data)
            } catch (error) {
              $datadog.addError(error)
            }
          }
        } else if (import.meta.client) {
          del(KEY)
        }
      } catch (error) {
        $datadog.addError(error)
      } finally {
        this.requestsInFlight.delete(inflightKey)
        this.isAggregateLoading = false
      }
    },

    async refreshConnectorAnalytics(workspaceId: string, connectorId: string) {
      if (!workspaceId || !connectorId) {
        return
      }
      const { $auth0, $datadog } = useNuxtApp()
      const { getAccessTokenSilently } = $auth0()

      try {
        const token = await getAccessTokenSilently()
        const client = useRpcClient(token)

        const res = await client.event.connector[':connectorId'].refresh.$get({
          param: {
            connectorId,
          },
          query: {
            workspace_id: workspaceId,
          },
        })

        const data = await res.json()

        this.$patch({
          retryAfterTimestamp:
            data?.retryAfterTimestamp || Number.POSITIVE_INFINITY,
          lastRefreshedAt: data?.lastRefreshedAt || 0,
          nextRefreshEstimate: data?.nextRefreshEstimate || 0,
          syncConnectorAfter:
            data?.nextRefreshEstimate || Number.POSITIVE_INFINITY,
        })
      } catch (error) {
        $datadog.addError(error)
        this.lastRefreshedAt = Number.POSITIVE_INFINITY
      }
    },

    setAnalyticsAggregate(
      connectorId: string,
      data: InsightItem | null | undefined,
    ) {
      const insightCounts = data || getDefaultInsightCounts(connectorId)

      const {
        policies,
        editTypeInsights,
        logicTypes,
        topicInsights,
        ...aggregate
      } = insightCounts

      this.connectors.set(connectorId, {
        connector_id: connectorId,
        aggregate: defu(aggregate, defaultAggregate) as AggregateState,
        editTypeInsights,
        error: null,
        lastRefreshedAt: insightCounts.lastRefreshedAt || null,
        loading: false,
        policies,
        topicInsights,
        logicTypes,
        updated: Date.now(),
      })
    },

    setLoadingState(connectorId: string, loading = false) {
      this.connectors.set(connectorId, {
        ...(this.connectors.get(connectorId) ||
          getDefaultInsightCounts(connectorId)),
        loading,
      })
    },

    updateConnectorAnalytics(
      connectorId: string,
      updates: Partial<ConnectorAnalyticsState>,
    ) {
      const currentState = this.connectors.get(connectorId)

      if (!currentState) {
        return
      }
      this.connectors.set(connectorId, {
        ...currentState,
        ...updates,
        updated: Date.now(),
      })
    },
  },
})

if (import.meta.hot)
  import.meta.hot.accept(
    acceptHMRUpdate(useConnectorAnalytics, import.meta.hot),
  )
